import { dayKey } from "./format";
import type { LiteTrade } from "./types";

// Mêmes conventions que calculateMetrics (trading-journal/analytics.ts) : seuls
// les trades clôturés comptent, net = brut + commission + swaps, et sans perte le
// profit factor vaut la somme des gains. Le Lite et l'Advanced affichent donc
// les mêmes chiffres.

export const net = (t: LiteTrade) => t.pnl + t.fees;
export const closedOnly = (trades: LiteTrade[]) => trades.filter(t => t.closed);

export interface Summary {
  count: number;
  netPnl: number;
  wins: number;
  losses: number;
  winRate: number;
  grossWin: number;
  grossLoss: number;
  profitFactor: number;
  avgWin: number;
  avgLoss: number;
  expectancy: number;
  best: number;
  worst: number;
  avgHoldMs: number;
}

export function summarize(all: LiteTrade[]): Summary {
  const trades = closedOnly(all);
  let netPnl = 0, wins = 0, losses = 0, grossWin = 0, grossLoss = 0, best = 0, worst = 0, hold = 0;
  for (const t of trades) {
    const n = net(t);
    netPnl += n;
    if (n > 0) { wins++; grossWin += n } else if (n < 0) { losses++; grossLoss += -n }
    best = Math.max(best, n);
    worst = Math.min(worst, n);
    hold += Math.max(0, new Date(t.closedAt).getTime() - new Date(t.openedAt).getTime());
  }
  const count = trades.length;
  return {
    count, netPnl, wins, losses,
    winRate: count ? wins / count : 0,
    grossWin, grossLoss,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin,
    avgWin: wins ? grossWin / wins : 0,
    avgLoss: losses ? grossLoss / losses : 0,
    expectancy: count ? netPnl / count : 0,
    best, worst,
    avgHoldMs: count ? hold / count : 0,
  };
}

export interface DayStat { key: string; date: Date; pnl: number; trades: LiteTrade[] }

/** Jours de trading, du plus récent au plus ancien (trades ouverts inclus dans la liste, exclus du P&L). */
export function byDay(trades: LiteTrade[]): DayStat[] {
  const map = new Map<string, DayStat>();
  for (const t of trades) {
    const k = dayKey(t.openedAt);
    let d = map.get(k);
    if (!d) {
      const date = new Date(t.openedAt); date.setHours(0, 0, 0, 0);
      d = { key: k, date, pnl: 0, trades: [] };
      map.set(k, d);
    }
    if (t.closed) d.pnl += net(t);
    d.trades.push(t);
  }
  return [...map.values()].sort((a, b) => b.key.localeCompare(a.key));
}

/** Courbe cumulée par jour + drawdown depuis le pic. */
export function equityCurve(trades: LiteTrade[], locale: string) {
  const days = byDay(closedOnly(trades)).reverse();
  let cum = 0, peak = 0;
  return days.map(d => {
    cum += d.pnl;
    peak = Math.max(peak, cum);
    return { key: d.key, date: d.date, label: d.date.toLocaleDateString(locale, { day: "numeric", month: "short" }), pnl: d.pnl, equity: Math.round(cum), drawdown: Math.round(cum - peak) };
  });
}

export function maxDrawdown(trades: LiteTrade[]) {
  return equityCurve(trades, "en-US").reduce((m, p) => Math.min(m, p.drawdown), 0);
}

export function groupBy<K extends string | number>(trades: LiteTrade[], key: (t: LiteTrade) => K) {
  const map = new Map<K, LiteTrade[]>();
  for (const t of trades) {
    const k = key(t);
    const arr = map.get(k);
    if (arr) arr.push(t); else map.set(k, [t]);
  }
  return map;
}

export function streaks(trades: LiteTrade[]) {
  const sorted = closedOnly(trades).sort((a, b) => a.openedAt.localeCompare(b.openedAt));
  let w = 0, l = 0, maxW = 0, maxL = 0;
  for (const t of sorted) {
    if (net(t) > 0) { w++; l = 0 } else { l++; w = 0 }
    maxW = Math.max(maxW, w); maxL = Math.max(maxL, l);
  }
  return { maxWin: maxW, maxLoss: maxL };
}
